import React, { useMemo } from "react";
import useSWR from "swr";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useDashboard } from "@/pages/Dashboard";
import KPICard from "@/components/KPICard";
import { TrendingUp, ArrowRight } from "lucide-react";

const fetcher = (url) => api.get(url).then((r) => r.data);

const CATEGORY_ORDER = [
  "Revenue & Sales",
  "Customer",
  "Membership & Voucher",
  "Operational",
  "Appointments & Lost Business",
  "Cost & Profitability",
  "Branch Benchmarking",
];

const SLUG_MAP = {
  "Revenue & Sales": "revenue-sales",
  "Customer": "customer",
  "Membership & Voucher": "membership-voucher",
  "Operational": "operational",
  "Appointments & Lost Business": "appointments",
  "Cost & Profitability": "cost-profitability",
  "Branch Benchmarking": "benchmarking",
};

export default function DashboardOverview() {
  const { month, period, branchCodes, cities, ownership, docTypes } = useDashboard() || {};
  const nav = useNavigate();

  const qs = new URLSearchParams();
  if (month) qs.set("month", month);
  if (period && period !== "month") qs.set("period", period);
  if (branchCodes?.length) qs.set("branches", branchCodes.join(","));
  if (cities?.length) qs.set("cities", cities.join(","));
  if (ownership?.length) qs.set("ownership", ownership.join(","));
  if (docTypes?.length) qs.set("doc_types", docTypes.join(","));
  const { data } = useSWR(
    month ? `/api/dashboard/summary?${qs.toString()}` : null,
    fetcher
  );

  const grouped = useMemo(() => {
    if (!data?.metrics) return {};
    const g = {};
    for (const m of data.metrics) (g[m.category] ||= []).push(m);
    return g;
  }, [data]);

  if (!data) return <div className="py-8 text-stone-500 font-mono text-sm">loading…</div>;

  return (
    <div>
      <div className="pt-6 mb-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-stone-500">
          Executive overview
        </div>
        <div className="mt-1 text-sm text-stone-600 max-w-2xl">
          Category-at-a-glance. Click any tile to dive deeper into period-over-period
          and year-over-year comparisons plus dimension breakdowns.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-6">
        {CATEGORY_ORDER.map((cat) => {
          const items = grouped[cat] || [];
          const slug = SLUG_MAP[cat];
          const headline = items[0];
          return (
            <button
              key={cat}
              onClick={() => nav(`/dashboard/${slug}`)}
              data-testid={`category-tile-${slug}`}
              className="group text-left rounded-lg border border-stone-200 bg-white p-6 hover:shadow-[0_10px_30px_rgba(60,74,62,0.08)] hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200"
            >
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-3.5 h-3.5 text-brand" />
                <div className="font-heading font-medium text-stone-900">{cat}</div>
                <div className="flex-1" />
                <ArrowRight className="w-4 h-4 text-stone-400 group-hover:text-brand transition-colors" />
              </div>
              {headline ? (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500">
                    {headline.name}
                  </div>
                  <KPIInline metric={headline} />
                </div>
              ) : (
                <div className="text-sm text-stone-500 font-mono">No metrics yet</div>
              )}
              <div className="mt-4 flex flex-wrap gap-1">
                {items.slice(1, 5).map((m) => (
                  <span key={m.metric_id} className="text-[10px] font-mono uppercase tracking-wide text-stone-500 border border-stone-200 rounded px-1.5 py-0.5">
                    {m.name}
                  </span>
                ))}
                {items.length > 5 && (
                  <span className="text-[10px] font-mono text-stone-400 px-1.5 py-0.5">+{items.length - 5} more</span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function KPIInline({ metric }) {
  const { current, prior, delta_pct, delta_abs, unit, trend_direction_good } = metric;
  // percent metrics: change in percentage points; others: relative change
  const change = unit === "percent" ? delta_abs : delta_pct;
  const changeSuffix = unit === "percent" ? " pts" : "%";
  const fmt = (v) => {
    if (v == null) return "—";
    if (unit === "currency") return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(v);
    if (unit === "percent") return `${v.toFixed(1)}%`;
    if (unit === "ratio") return v.toFixed(2);
    if (unit === "count") return new Intl.NumberFormat("en-IN").format(Math.round(v));
    return v.toLocaleString();
  };
  const isUp = (change ?? 0) > 0;
  const isGood = (isUp && trend_direction_good === "up") || (!isUp && trend_direction_good === "down");
  const color = change === 0 || change == null ? "text-stone-500" : isGood ? "text-brand-good" : "text-brand-bad";
  return (
    <>
      <div className="font-mono text-2xl font-medium text-stone-900 mt-1">
        {fmt(current)}
      </div>
      <div className="text-xs font-mono mt-1 flex items-center gap-2">
        <span className={color}>
          {change == null ? "—" : `${isUp ? "+" : ""}${change.toFixed(1)}${changeSuffix}`}
        </span>
        <span className="text-stone-500">vs {fmt(prior)}</span>
      </div>
    </>
  );
}
